import type { StatusVariants } from "@/components/molecules";
import type { PaginatedResponse } from "@/types/api";

export enum EntityEnum {
  GROUP = "Group",
  BRAND = "Brand",
  BRANCH = "Branch",
}

export interface CreditReportEntity {
  entityId: string;
  entityName: string;
  typeOfEntity: EntityEnum;
  totalCashbackEarned: number;
  totalCashbackSpent: number;
  startingBalance: number;
  closingBalance: number;
  cashbackExpired?: number | null;
}

export type CreditBalanceFullReport = PaginatedResponse<CreditReportEntity> & {
  totalDinersTopUps: number;
};

export enum TOP_UPS_STATUS {
  APPROVED = "APPROVED",
  PENDING = "PENDING",
  REJECTED = "REJECTED",
}

export const STATUS_TOPUPS_VARIANT_MAP: StatusVariants = {
  Approved: { badge: "success", dot: "#65A30D" },
  Pending: { badge: "info", dot: "#D97706" },
  Rejected: { badge: "warning", dot: "#DC2626" },
};

export const topUpsStatusMap: Record<string, TOP_UPS_STATUS> = {
  Approved: TOP_UPS_STATUS.APPROVED,
  Pending: TOP_UPS_STATUS.PENDING,
  Rejected: TOP_UPS_STATUS.REJECTED,
};

export interface ITopUp {
  id: string;
  dinerId: string;
  createdAt: string;
  approvedAmount?: number | null;
  name: string;
  requestedAmount?: number | null;
  processedAt?: string | null;
  reason: string;
  status: TOP_UPS_STATUS;
  actions?: boolean;
}

export interface ExtendedPaginatedResponse<T> extends PaginatedResponse<T> {
  totalRequestedAmount: number;
  totalApprovedAmount: number;
  totalDiners: number;
}

export type AdminTopUpsReportingResponse = ExtendedPaginatedResponse<ITopUp>;

export const SettlementsEnum = {
  ...EntityEnum,
  LEGAL_ENTITY: "Legal entity",
} as const;

export type SettlementsEnum =
  (typeof SettlementsEnum)[keyof typeof SettlementsEnum];

export interface ISettlement {
  entityId: string;
  entityName: string;
  entityType?: SettlementsEnum;
  groupName: string;
  brandName?: string;
  branchName?: string;
  startingBalance: number | null;
  totalTips: number;
  totalExcludedItems: number;
  payableAmount: number | null;
  transferedAmount: number | null;
  currentBalance: number | null;
  groupReferenceNumber?: string;
}

export interface ExtendedSettlementsOverviewResponse<T>
  extends PaginatedResponse<T> {
  totalTips: number;
  totalExcludedItems: number;
  totalPayableAmount: number;
  totalTransferedAmount: number;
  totalBalance: number;
}

export type AdminSettlementOverviewReportingResponse =
  ExtendedSettlementsOverviewResponse<ISettlement>;

export interface AdminAccountsDashboardResponse {
  totalLegalEntities: number;
  totalLegalEntitiesCreatedInPeriod: number;
  totalGroups: number;
  totalGroupsCreatedInPeriod: number;
  totalBrands: number;
  totalBrandsCreatedInPeriod: number;
  totalBranches: number;
  totalBranchesCreatedInPeriod: number;
  charts: Array<{
    date: string;
    totalNewBrands: number;
    totalNewBranches: number;
  }>;
}

export interface AdminDinersDashboardResponse {
  totalDiners: number;
  totalCreatedInPeriod: number;
  totalDinersWithTransactions: number;
  totalDinersWithTransactionsPercent: number;
  totalPendingDiners: number;
  totalPendingDinersPercent: number;
  avgPurchases: number;
  avgBasket: number;
  charts: Array<{
    date: string;
    totalNewDiners: number;
    totalNewTransactions: number;
  }>;
}

export interface AdminSettlementDashboardResponse {
  totalActiveCredits: number;
  totalBrandsWithActiveCredits: number;
  totalCreditConsumed: number;
  percentConsumed: number;
  totalCreditRemaining: number;
  percentRemaining: number;
  totalUpcoming: number;
  totalBrandsWithUpcomingCredits: number;
  totalPayable: number;
  totalPayableGroups: number;
  totalTransfered: number;
  totalTransferedGroups: number;
  totalBalance: number;
  totalBalanceGroups: number;
  charts: Array<{
    date: string;
    creditConsumed: number;
    creditRemaining: number;
    ideal: number;
  }>;
}

export interface AdminTransactionsDashboardResponse {
  totalTransactions: number;
  completedOrders: number;
  totalAmount: number;
  avgBasket: number;
  totalFailed: number;
  failedPercent: number;
  totalCashBackEarned: number;
  totalCashbackAddedBySpice: number;
  totalCashBackSpent: number;
  totalSpentCashbackPercent: number;
  charts: Array<{
    date: string;
    totalEarnedCashback: number;
    totalSpentCashback: number;
    totalExpiredCashback: number;
    totalCashbackTopup: number;
  }>;
}

type ChartValue = {
  name: string;
  value: null | null;
  brandId?: string;
};

export interface AdminOverviewDashboardResponse {
  // KPI cards
  threshold_limit: number;
  totalBrands: number;
  totalBrandsGrowPercent: number | null;

  totalDiners: number;
  totalDinersGrowPercent: number | null;

  totalTransactionsAmount: number;
  totalTransactionsAmountGrowPercent: number | null;

  totalRemainingCredit: number;
  totalRemainingCreditGrowthPercent: number | null;

  // Chart A – Brands over credit threshold limit
  topBrandsOverCredit: Array<
    ChartValue & {
      totalConsumed: number;
      totalAllocated: number;
    }
  >;

  // Chart B – Top credit consuming brands
  topBrandsConsuming: Array<
    ChartValue & {
      threshold_limit: number;
    }
  >;
  creditThresholdValue: number;

  // Widget C – Inactive brands
  topInactiveBrands: Array<ChartValue>;

  // Widget D – Slow burning brands
  topSlowBurningBrands: Array<ChartValue>;
}
